import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { delay, switchMap, takeUntil } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PackService } from '@services/pack/pack.service';
import { PackLite } from '@shared/models/generic/pack-lite.model';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from '@services/config.service';
import { RefDataService } from '@services/ref-data.service';
import { PackTabComponent } from './pack-tab/pack-tab.component';
import { NotificationManagerService } from '@shared/services/notification-manager.service';
import { HttpErrorResponse } from '@angular/common/http';
import { TextPopinComponent } from '@shared/components/text-popin/text-popin.component';
import { PackRecipeTabComponent } from './pack-recipe-tab/pack-recipe-tab.component';
import { PublicationDateService } from '@shared/services/publication-date.service';
import { SpecificFields } from '@clients/models/client.model';
import { MatDialog } from '@angular/material/dialog';
import { SharedClientConfigurationService } from '@services/client/sharedClientConfiguration.service';
import { hasTargetDomainForBinaries } from '@packs/utils/pack.utils';

@Component({
  selector: 'app-pack-parent-component',
  templateUrl: './pack.component.html',
  standalone: false,
})
export class PackComponent implements OnInit, OnDestroy {
  @ViewChild('packTabComponent')
    packTabComponent?: PackTabComponent;

  @ViewChild('packRecipeTabComponent')
    packRecipeTabComponent?: PackRecipeTabComponent;

  isEdit = false;
  packTitle = '';
  packId = '';
  formPack = this.formBuilder.group({});
  isSubmitted = false;
  lang = 'en';
  pack: PackLite = {} as PackLite;
  destroy$ = new Subject<void>();
  newPackLink = '#/references/packs/edit/';

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly packService: PackService,
    private readonly translateService: TranslateService,
    private readonly configService: ConfigService,
    private readonly refDataService: RefDataService,
    private readonly notificationManager: NotificationManagerService,
    private readonly dialog: MatDialog,
    private readonly publicationDateService: PublicationDateService,
    private readonly sharedClientConfigService: SharedClientConfigurationService,
  ) {}

  ngOnInit(): void {
    this.lang = this.configService.currentLanguage;
    this.formPack = this.initForm();
    this.buildPackLink();
    this.route.params
      .pipe(delay(0))
      .pipe(
        switchMap((params: Params): Observable<PackLite | undefined> => {
          if (params.packId) {
            this.packId = params.packId;
            this.isEdit = true;
            return this.packService.getPack(this.packId);
          } else {
            this.packId = '';
            this.packTitle = 'MENU.PACKS_NEW';
            this.isEdit = false;
            return of(undefined);
          }
        }),
      )
      .subscribe(
        (pack: PackLite | undefined) => {
          if (pack) {
            this.pack = pack;
            if (this.isEdit) {
              this.packTitle = this.translateService.instant('REFDATA.PACKS.TABS.PACK') + ': ' + Object.values(pack.name[pack.market])[0];
            }
            this.formPack = this.initForm();
            this.lang = this.formPack.controls.lang.value ?? this.lang;
            this.pack.title = this.getPackTitle();
            this.pack.lang = this.getPackLang();
          }
        },
        (e: HttpErrorResponse) => {
          if (e.status === 404) {
            this.router.navigate(['404']);
          }
        },
      );
  }

  ngOnDestroy(): void {
    this.publicationDateService.formSubmitStatus(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  getPackLang(): string {
    if (this.pack?.name) {
      return Object.keys(this.pack.name[this.pack.market])[0];
    }
    return '';
  }

  getPackTitle(): string {
    if (this.pack?.name) {
      return Object.values(this.pack.name[this.pack.market])[0];
    }
    return '';
  }

  save(): void {
    // Trigger publication date input validation on submit
    this.publicationDateService.formSubmitStatus(true);
    if (this.formPack.invalid) {
      return;
    }
    this.isSubmitted = true;
    const pack: PackLite = {
      key: this.pack?.key,
      status: this.formPack.controls.status.value ?? 'DRAFT',
      domain: this.formPack.controls.domain.value ?? '',
      lang: this.formPack.controls.lang.value ?? '',
      title: this.formPack.controls.title.value ?? '',
      market: this.formPack.controls.market.value ?? '',
      description: this.formPack.controls.description.value ?? '',
      resourceMedias: this.formPack.controls.resourceMedias.value ?? [],
      type: this.formPack.controls.type.value ?? 'TECHNIQUE',
      isPremium: this.formPack.controls.isPremium.value,
      name: {},
      publicationStartDate: this.formPack.controls.publicationStartDate.value
        ? new Date(this.formPack.controls.publicationStartDate.value).toISOString()
        : undefined,
      publicationEndDate: this.formPack.controls.publicationEndDate.value
        ? new Date(this.formPack.controls.publicationEndDate.value).toISOString()
        : undefined,
      recipesByApplianceGroup: this.formPack.controls.recipesByApplianceGroup.value ?? [],
    } as PackLite;
    pack.name[pack.market] = {};
    pack.name[pack.market][pack.lang] = pack.title;
    if (this.isEdit) {
      this.update(pack);
    } else {
      this.create(pack);
    }
  }

  create(pack: PackLite): void {
    this.packService.createPack(pack).subscribe(
      (packCreated: PackLite) => {
        this.isSubmitted = false;
        this.notificationManager.displaySuccess(this.translateService.instant('REFDATA.PACKS.MSG.SUCCESS.CREATE'));
        this.router.navigateByUrl(this.newPackLink + packCreated.key);
      },
      (err: any) => {
        this.notificationManager.displayHttpError(err);
      },
    );
  }

  buildPackLink(): void {
    this.sharedClientConfigService.specificFields$.pipe(takeUntil(this.destroy$)).subscribe((fields: SpecificFields) => {
      if (fields?.newPackScreensEnable) {
        this.newPackLink = '/references/packs/edit/';
      }
    });
  }

  update(pack: PackLite): void {
    const nbAttachedRecipes = this.packRecipeTabComponent?.totalElements ?? 0;
    const isPublished = this.formPack.controls.status.value === 'PUBLISHED';

    if (isPublished && nbAttachedRecipes <= 0) {
      this.notificationManager.displayError(this.translateService.instant('REFDATA.PACKS.WARNINGS.PUBLISHED_MUST_HAVE_ONE_RECIPE_CONTENT'));
      return;
    }

    if (hasTargetDomainForBinaries(this.pack) && isPublished && nbAttachedRecipes > 0) {
      this.packService.publish(this.pack?.key ?? '').subscribe(
        (_msg) => {
          this.updatePack(pack);
        },
        (error: HttpErrorResponse) => {
          this.handlePublishPackErrors(error);
        },
      );
    } else {
      this.updatePack(pack);
    }
  }

  updatePack(pack: PackLite): void {
    this.packService.updatePack(pack).subscribe(
      (_packUpdated: PackLite) => {
        this.isSubmitted = false;
        this.notificationManager.displaySuccess(this.translateService.instant('REFDATA.PACKS.MSG.SUCCESS.UPDATE'));
        const packTargetDomains = this.packTabComponent?.packTargetDomainsComponent?.form.value.targetDomains;
        this.refDataService.putTargetDomains('packs', pack.key, packTargetDomains).subscribe(
          () => {
            this.refreshPack();
          },
          (error) => {
            this.notificationManager.displayHttpError(error);
            this.refreshPack();
          },
        );
      },
      (error) => {
        this.notificationManager.displayHttpError(error);
      },
    );
  }

  publishPack(): void {
    if ((this.packRecipeTabComponent?.totalElements ?? 0) > 0) {
      this.packService.publish(this.pack?.key ?? '').subscribe(
        () => {
          this.notificationManager.displaySuccess(this.translateService.instant('REFDATA.PACKS.MSG.SUCCESS.PUBLISH'));
          this.refreshPack();
        },
        (error: HttpErrorResponse) => {
          this.handlePublishPackErrors(error);
        },
      );
    }
  }

  refreshPack(): void {
    this.packService.getPack(this.pack?.key ?? '').subscribe((pack) => {
      this.pack = pack;
      this.pack.title = this.getPackTitle();
      this.pack.lang = this.getPackLang();
      this.refreshForm();
    });
  }

  handlePublishPackErrors(error: HttpErrorResponse): void {
    this.notificationManager.displayHttpError(error, undefined, [
      { param: '%(lang)', value: this.pack?.lang ?? '' },
      { param: '%(market)', value: this.pack?.market ?? '' },
    ]);
  }

  cancel(): void {
    const data: any = {
      title: 'MODAL_CANCEL_TITLE',
      text: 'MODAL_CANCEL_MSG',
      cancelBtn: 'NO',
      validateBtn: 'YES',
    };
    const dialogRef = this.dialog.open(TextPopinComponent, {
      data,
    });
    dialogRef.afterClosed().subscribe((response: any) => {
      this.resetForm(response);
    });
  }

  resetForm(response: boolean): void {
    if (response) {
      this.formPack = this.initForm();
      this.lang = this.formPack.controls.lang.value ?? this.lang;
    }
  }

  private initForm(): UntypedFormGroup {
    // Build the form
    return this.formBuilder.group({
      domain: [this.pack?.domain ?? '', Validators.required],
      market: [this.pack?.market ?? '', Validators.required],
      lang: [this.getPackLang(), Validators.required],
      isPremium: this.pack?.isPremium ?? false,
      description: this.pack?.description ?? '',
      title: [this.getPackTitle(), Validators.required],
      type: this.pack?.type ?? 'TECHNIQUE',
      status: this.pack?.status ?? 'DRAFT',
      modificationDate: this.pack?.modificationDate,
      resourceMedias: [this.pack?.resourceMedias ?? []],
      binaries: this.pack?.binaries ?? [],
      archives: this.pack?.archives ?? [],
      publicationStartDate: this.pack?.publicationStartDate,
      publicationEndDate: this.pack?.publicationEndDate,
      recipesByApplianceGroup: this.pack?.recipesByApplianceGroup ?? [],
    });
  }

  private refreshForm(): void {
    return this.formPack.patchValue({
      domain: this.pack?.domain ?? '',
      market: this.pack?.market ?? '',
      lang: this.getPackLang(),
      isPremium: this.pack?.isPremium ?? false,
      description: this.pack?.description ?? '',
      title: this.getPackTitle(),
      type: this.pack?.type ?? 'TECHNIQUE',
      status: this.pack?.status ?? 'DRAFT',
      modificationDate: this.pack?.modificationDate ?? '',
      resourceMedias: this.pack?.resourceMedias ?? [],
      binaries: this.pack?.binaries ?? [],
      archives: this.pack?.archives ?? [],
      publicationStartDate: this.pack?.publicationStartDate ?? '',
      publicationEndDate: this.pack?.publicationEndDate ?? '',
      recipesByApplianceGroup: this.pack?.recipesByApplianceGroup ?? [],
    });
  }
}
